import React from 'react';
import { alpha, Box, Paper, Skeleton, Theme, Typography } from '@mui/material';
import {
    deltaColor,
    formatMoney,
    formatPercent,
    formatSignedMoney,
} from './expenses-summary-delta';

export type ExpensesSummaryKpiData = {
    monthTotal: number;
    previousMonthTotal: number;
    delta: number;
    deltaPct: number | null;
    sameMonthLastYearTotal: number;
    yearToDateTotal: number;
};

function KpiTile({
    label,
    value,
    subtitle,
    color,
    loading,
}: {
    label: string;
    value: string;
    subtitle?: string;
    // Palette path for the value text; defaults to the standard info accent.
    color?: string;
    loading?: boolean;
}) {
    const accent = color ?? 'info.main';
    return (
        <Paper
            elevation={0}
            sx={{
                minWidth: 190,
                flex: '1 1 190px',
                px: 2,
                py: 1.25,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderTop: '3px solid',
                borderTopColor: accent,
                bgcolor: (theme: Theme) =>
                    alpha(theme.palette.info.main, 0.05),
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    fontWeight: 600,
                    display: 'block',
                }}
            >
                {label}
            </Typography>
            {loading ? (
                <Skeleton
                    variant="text"
                    width={110}
                    sx={{ transform: 'none', height: 28, mt: 0.5 }}
                />
            ) : (
                <>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: accent, lineHeight: 1.3 }}
                    >
                        {value}
                    </Typography>
                    {subtitle ? (
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                        >
                            {subtitle}
                        </Typography>
                    ) : null}
                </>
            )}
        </Paper>
    );
}

export function ExpensesSummaryKpis({
    kpis,
    loading,
    monthLabel,
    previousMonthLabel,
    year,
}: {
    kpis: ExpensesSummaryKpiData;
    loading?: boolean;
    monthLabel: string;
    previousMonthLabel: string;
    year: number;
}) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <KpiTile
                label={monthLabel}
                value={formatMoney(kpis.monthTotal)}
                loading={loading}
            />
            <KpiTile
                label={previousMonthLabel}
                value={formatMoney(kpis.previousMonthTotal)}
                loading={loading}
            />
            <KpiTile
                label={'Cambio vs mes anterior'}
                value={formatSignedMoney(kpis.delta)}
                subtitle={formatPercent(kpis.deltaPct)}
                color={deltaColor(kpis.delta)}
                loading={loading}
            />
            <KpiTile
                label={`Mismo mes ${year - 1}`}
                value={formatMoney(kpis.sameMonthLastYearTotal)}
                loading={loading}
            />
            <KpiTile
                label={`Acumulado ${year}`}
                value={formatMoney(kpis.yearToDateTotal)}
                loading={loading}
            />
        </Box>
    );
}
